import React, { useState, useEffect, useMemo, useReducer, useCallback } from 'react';
import { Search, Clock, Star, X } from 'lucide-react';
import { formatCurrency } from '../utils/currencyUtils';
import { EquipmentResolver } from '../utils/equipmentResolver';
import { getItemsFromSources } from '../data/sources/initializeSources';
import { systemBus, EVENTS } from '../services/systemBus';
import { safeLocalStorageSet } from '../utils/storageValidator';
import MobileModal from './MobileModal';
import { useModalEscape } from '../hooks/useModalEscape';
// [PHASE B / #003]: Search + display-name helpers extracted so tests
//                   exercise production code (Rule 22).
import {
  matchesAllTokens,
  formatCategoryName,
  formatRecentFavoriteName,
} from '../utils/itemSearchUtils';
// [PHASE B / #019 — B2-5]: Category icon lookup driven by the
//   BASE_ITEM_CATEGORIES data block.
import { getBaseItemCategoryIcon } from '../constants/baseItemCategories';

// [2026-05-25]: Render a property-rich summary line for an item row.
//   Returns short pills/text covering damage (weapons), AC (armor),
//   rarity + magic bonus (magic items), charges (consumables/magic),
//   weight, value. Used by find-mode browse rows so the user can compare
//   items without opening each one. Pure: input is a BASE_ITEMS row.
const renderItemPropertyBadges = (item, formatCurrency) => {
  if (!item) return null;
  const badges = [];
  if (item.damage) badges.push({ label: `${item.damage}${item.damageType ? ' ' + item.damageType : ''}`, tone: 'red' });
  if (item.armorClass != null) badges.push({ label: `AC ${item.armorClass}${item.armorType ? ` (${item.armorType})` : ''}`, tone: 'blue' });
  if (item.rarity) badges.push({ label: item.rarity, tone: 'amber' });
  if (item.magicalBonus) badges.push({ label: `+${item.magicalBonus}`, tone: 'purple' });
  if (item.requiresAttunement) badges.push({ label: 'attune', tone: 'violet' });
  if (item.maxCharges) badges.push({ label: `${item.charges ?? item.maxCharges}/${item.maxCharges} chg`, tone: 'cyan' });
  if (Array.isArray(item.properties) && item.properties.length) {
    badges.push({ label: item.properties.join(', '), tone: 'slate' });
  }
  const tones = {
    red: 'bg-red-900/40 text-red-200 border-red-700/40',
    blue: 'bg-blue-900/40 text-blue-200 border-blue-700/40',
    amber: 'bg-amber-900/40 text-amber-200 border-amber-700/40',
    purple: 'bg-purple-900/40 text-purple-200 border-purple-700/40',
    violet: 'bg-violet-900/40 text-violet-200 border-violet-700/40',
    cyan: 'bg-cyan-900/40 text-cyan-200 border-cyan-700/40',
    slate: 'bg-slate-800 text-slate-300 border-slate-600/40',
  };
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {badges.map((b, i) => (
        <span
          key={i}
          className={`px-1.5 py-0.5 rounded text-[10px] border ${tones[b.tone] || tones.slate}`}
        >
          {b.label}
        </span>
      ))}
      {typeof item.weight === 'number' && (
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-800 text-gray-400 border border-gray-700">
          {item.weight} lb
        </span>
      )}
      {item.baseValue != null && (
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-800 text-yellow-300 border border-gray-700">
          {formatCurrency(item.baseValue)}
        </span>
      )}
    </div>
  );
};

const SearchableItemSelector = ({
  BASE_ITEMS,
  STARTER_PACKS,
  selectedCategory,
  selectedItem,
  onCategoryChange,
  onItemChange,
  showPacks = true,
  // [2026-05-25]: When true, render the category browser INLINE (no
  // popup), hide the Recent/Favorites/Browse-button block, and show
  // every item's properties on the row. Used by ItemDialog mode='find'.
  findMode = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [recentItems, setRecentItems] = useState([]);
  const [favoriteItems, setFavoriteItems] = useState([]);
  // [2026-05-25]: In find mode the browser is always open inline.
  const [showCategories, setShowCategories] = useState(findMode);
  // [PHASE 10 - NOV 20, 2025]: Track category within browser modal for two-stage navigation
  // [WHY]: Users need to see items within a category before selecting one
  const [browserCategory, setBrowserCategory] = useState(null);

  // [APR 2026]: Standardize Escape dismissal for the Browse Categories modal.
  // [WHY]: Mirrors the X close button which clears both state values.
  // [UI-MOBILEMODAL-ADOPTION wave 2c — 2026-07-05]: the standalone (!findMode)
  // browser now wraps MobileModal, which registers useModalEscape itself. The
  // findMode inline browser is NOT wrapped by MobileModal, so it keeps its own
  // Escape handler — gated on findMode so the two never double-register.
  const closeCategoryBrowser = useCallback(() => {
    setBrowserCategory(null);
    setShowCategories(false);
  }, []);

  useModalEscape(findMode && showCategories, closeCategoryBrowser);

  // [PHASE 5 - NOV 19, 2025]: Force re-render mechanism for reactive item selection
  // [PURPOSE]: Enables selector to react to source changes for real-time updates
  // [WHY]: When sources are enabled/disabled, available items list needs to refresh
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 5 - NOV 19, 2025]: Event subscriptions for source changes
  // [PURPOSE]: Subscribe to source enable/disable events for real-time item list updates
  // [WHY]: When user enables Tasha's/Xanathar's sources, new items should appear immediately
  // [INTEGRATION]: Ensures item selection reflects current enabled sources
  useEffect(() => {
    const handleSourceEvent = (eventName, data) => {
      // Force re-render to update available items from sources
      forceUpdate();
    };

    // [SOURCE_ENABLED]: Content source was enabled (adds items)
    const unsubscribeSourceEnabled = systemBus.on(EVENTS.SOURCE_ENABLED, (data) =>
      handleSourceEvent('SOURCE_ENABLED', data));

    // [SOURCE_DISABLED]: Content source was disabled (removes items)
    const unsubscribeSourceDisabled = systemBus.on(EVENTS.SOURCE_DISABLED, (data) =>
      handleSourceEvent('SOURCE_DISABLED', data));

    return () => {
      unsubscribeSourceEnabled();
      unsubscribeSourceDisabled();
    };
  }, []);

  // Load recent and favorite items from localStorage
  useEffect(() => {
    const savedRecent = localStorage.getItem('tabletopInventory_recentItems');
    const savedFavorites = localStorage.getItem('tabletopInventory_favoriteItems');
    
    if (savedRecent) {
      try {
        setRecentItems(JSON.parse(savedRecent));
      } catch (e) {
        console.error('Failed to load recent items:', e);
      }
    }
    
    if (savedFavorites) {
      try {
        setFavoriteItems(JSON.parse(savedFavorites));
      } catch (e) {
        console.error('Failed to load favorite items:', e);
      }
    }
  }, []);

  // Save item to recent when selected
  const addToRecent = (category, itemKey) => {
    const itemData = { category, itemKey, timestamp: Date.now() };
    const newRecent = [itemData, ...recentItems.filter(
      item => !(item.category === category && item.itemKey === itemKey)
    )].slice(0, 10); // Keep only 10 most recent
    
    setRecentItems(newRecent);
    safeLocalStorageSet('tabletopInventory_recentItems', JSON.stringify(newRecent));
  };

  // Toggle favorite status
  const toggleFavorite = (category, itemKey) => {
    const itemData = { category, itemKey };
    const isFavorite = favoriteItems.some(
      item => item.category === category && item.itemKey === itemKey
    );
    
    let newFavorites;
    if (isFavorite) {
      newFavorites = favoriteItems.filter(
        item => !(item.category === category && item.itemKey === itemKey)
      );
    } else {
      newFavorites = [...favoriteItems, itemData];
    }
    
    setFavoriteItems(newFavorites);
    safeLocalStorageSet('tabletopInventory_favoriteItems', JSON.stringify(newFavorites));
  };

  // [PHASE B / #003]: Search now uses strict word-token substring match
  //                   (`matchesAllTokens` in src/utils/itemSearchUtils.js).
  //                   The previous in-order character match across the
  //                   full searchText (which includes long descriptions)
  //                   produced false positives like searching "cloak" →
  //                   "Moon Sickle" / "backpack" / "manacles". The shared
  //                   helper makes the contract testable per Rule 22.

  // [PHASE 5 - NOV 19, 2025]: Get all searchable items from BASE_ITEMS + enabled sources
  // [PURPOSE]: Merge SRD items with source items (Tasha's, Xanathar's, custom sources)
  // [WHY]: Users want to search across all available items, not just SRD
  // [INTEGRATION]: Reactive to source enable/disable via forceUpdate dependency
  const allItems = useMemo(() => {
    const items = [];

    // [PHASE 5 - NOV 19, 2025]: Merge BASE_ITEMS with items from enabled sources
    const sourceItems = getItemsFromSources();
    const mergedItems = { ...BASE_ITEMS };

    // Merge source items into BASE_ITEMS structure (source items take precedence for duplicates)
    Object.entries(sourceItems).forEach(([category, categoryItems]) => {
      if (!mergedItems[category]) {
        mergedItems[category] = {};
      }
      mergedItems[category] = { ...mergedItems[category], ...categoryItems };
    });

    // Add all merged items (BASE_ITEMS + sources)
    Object.entries(mergedItems).forEach(([category, categoryItems]) => {
      Object.entries(categoryItems).forEach(([itemKey, item]) => {
        // [D.1 APR 2026]: Use EquipmentResolver.formatDisplayName so magic
        // weapons render as "+1 Longsword" instead of "longsword plus 1".
        const displayName = item.name || EquipmentResolver.formatDisplayName(itemKey) || itemKey.replace(/_/g, ' ');
        items.push({
          category,
          itemKey,
          item,
          displayName,
          searchText: `${category} ${displayName} ${item.description || ''}`.toLowerCase(),
          type: 'item'
        });
      });
    });
    
    // Add starter packs if enabled
    if (showPacks && STARTER_PACKS) {
      Object.entries(STARTER_PACKS).forEach(([packKey, pack]) => {
        items.push({
          category: 'packs',
          itemKey: packKey,
          item: pack,
          displayName: pack.name,
          searchText: `pack starter ${pack.name} ${pack.description || ''}`.toLowerCase(),
          type: 'pack'
        });
      });
    }


    return items;
  }, [BASE_ITEMS, STARTER_PACKS, showPacks, forceUpdate]); // [PHASE 5 - NOV 19, 2025]: Added forceUpdate for source reactivity

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return [];
    
    return allItems.filter(item =>
      matchesAllTokens(item.searchText, searchQuery)
    ).slice(0, 20); // Limit to 20 results for performance
  }, [allItems, searchQuery]);

  // [PHASE 10 - NOV 20, 2025]: Get items for browser category (used in modal)
  // [WHY]: browserCategory is used for navigating within the modal, not the selected item
  // [NOTE]: Returns [] if packs category or invalid category
  // [FIX JAN 2026]: Use allItems (merged BASE_ITEMS + sources) instead of just BASE_ITEMS
  // [WHY]: Category browser was only showing SRD items, not source items like Tasha's/Xanathar's
  const categoryItems = useMemo(() => {
    if (!browserCategory || browserCategory === 'packs') return [];
    // Filter from allItems to get items in this category (includes source items)
    return allItems
      .filter(item => item.category === browserCategory && item.type === 'item')
      .map(item => [item.itemKey, item.item]);
  }, [allItems, browserCategory]);

  // [PHASE 10 - NOV 20, 2025]: Get pack items for browser (used in modal)
  // [WHY]: Starter packs have different structure than BASE_ITEMS
  const packItems = useMemo(() => {
    if (browserCategory !== 'packs' || !STARTER_PACKS) return [];
    return Object.entries(STARTER_PACKS);
  }, [STARTER_PACKS, browserCategory]);

  // Format item display
  const formatItemDisplay = (category, itemKey, item) => {
    let displayText = `${item.icon || '📄'} ${itemKey.replace(/_/g, ' ')}`;
    
    if (category === 'weapons' || category === 'armor') {
      displayText += ` (${item.size[0]}x${item.size[1]})`;
    } else if (category === 'containers') {
      displayText += ` (${item.containerSlots[0]}x${item.containerSlots[1]} slots)`;
      if (item.extradimensional) displayText = `✨ ${displayText}`;
    } else {
      displayText += ` (${formatCurrency(item.baseValue || 0)})`;
    }
    
    return displayText;
  };

  // [PHASE B / #019 — B2-5]: Icon lookup via BASE_ITEM_CATEGORIES data
  //   block; the 'packs' bucket is a virtual category for STARTER_PACKS
  //   (not a BASE_ITEMS storage key) so it stays inline.
  const getCategoryIcon = (category) => {
    if (category === 'packs') return '🎁';
    return getBaseItemCategoryIcon(category);
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative" data-tutorial="add-item-search">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <Search size={20} />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search items... (e.g. 'sword', 'rope', 'healing')"
          className="w-full pl-10 pr-10 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="item-search-input"
          autoFocus
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Search Results */}
      {searchQuery && filteredItems.length > 0 && (
        <div className="bg-gray-800 rounded-lg max-h-60 overflow-y-auto" data-testid="item-search-results">
          <div className="p-2 text-xs text-gray-400 border-b border-gray-700">
            Found {filteredItems.length} items
          </div>
          {filteredItems.map((result, index) => (
            <button
              key={`${result.category}-${result.itemKey}-${index}`}
              data-testid={`item-search-result-${result.itemKey}`}
              data-item-key={result.itemKey}
              data-category={result.category}
              onClick={() => {
                onCategoryChange(result.category);
                onItemChange(result.itemKey);
                addToRecent(result.category, result.itemKey);
                setSearchQuery('');
              }}
              className="w-full text-left px-3 py-2 hover:bg-gray-700 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{result.item.icon || getCategoryIcon(result.category)}</span>
                <div>
                  <div className="text-sm font-medium">{result.displayName}</div>
                  <div className="text-xs text-gray-400">{formatCategoryName(result.category)}</div>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(result.category, result.itemKey);
                }}
                className="p-1 hover:bg-gray-600 rounded"
              >
                <Star 
                  size={16} 
                  className={favoriteItems.some(
                    item => item.category === result.category && item.itemKey === result.itemKey
                  ) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'}
                />
              </button>
            </button>
          ))}
        </div>
      )}

      {/* Quick Access Section - Only show when not searching AND NOT in find mode.
          In find mode the inline category browser (below) replaces these
          shortcuts so the user sees categories + items directly. */}
      {!searchQuery && !findMode && (
        <div data-tutorial="add-item-favorites">
          {/* Favorite Items */}
          {favoriteItems.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2 text-sm text-yellow-400">
                <Star size={16} />
                <span className="font-medium">Favorites</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {favoriteItems.slice(0, 4).map((favItem) => {
                  const item = favItem.category === 'packs' 
                    ? STARTER_PACKS[favItem.itemKey]
                    : BASE_ITEMS[favItem.category]?.[favItem.itemKey];
                  
                  if (!item) return null;
                  
                  return (
                    <button
                      key={`fav-${favItem.category}-${favItem.itemKey}`}
                      onClick={() => {
                        onCategoryChange(favItem.category);
                        onItemChange(favItem.itemKey);
                        addToRecent(favItem.category, favItem.itemKey);
                      }}
                      className="p-2 bg-gray-700 hover:bg-gray-600 rounded text-left text-sm transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span>{item.icon || getCategoryIcon(favItem.category)}</span>
                        <span className="truncate">{formatRecentFavoriteName(favItem.itemKey, item)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Items */}
          {recentItems.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2 text-sm text-gray-400">
                <Clock size={16} />
                <span className="font-medium">Recent</span>
              </div>
              <div className="space-y-1">
                {recentItems.slice(0, 3).map((recentItem) => {
                  const item = recentItem.category === 'packs' 
                    ? STARTER_PACKS[recentItem.itemKey]
                    : BASE_ITEMS[recentItem.category]?.[recentItem.itemKey];
                  
                  if (!item) return null;
                  
                  return (
                    <button
                      key={`recent-${recentItem.category}-${recentItem.itemKey}`}
                      onClick={() => {
                        onCategoryChange(recentItem.category);
                        onItemChange(recentItem.itemKey);
                        addToRecent(recentItem.category, recentItem.itemKey);
                      }}
                      className="w-full p-2 hover:bg-gray-700 rounded text-left text-sm transition-colors flex items-center gap-2"
                    >
                      <span>{item.icon || getCategoryIcon(recentItem.category)}</span>
                      <span>{formatRecentFavoriteName(recentItem.itemKey, item)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Browse by Category Button */}
          <button
            onClick={() => setShowCategories(true)}
            className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-center transition-colors"
            data-tutorial="add-item-browse"
          >
            Browse by Category →
          </button>
        </div>
      )}

      {/* [PHASE 10 - NOV 20, 2025]: Two-Stage Category Browser */}
      {/* [PURPOSE]: Show categories → items (two-stage navigation) */}
      {/* [WHY]: Users need to browse items within a category before selecting */}
      {/* [2026-05-25]: In findMode this renders INLINE (no fixed overlay, no
          backdrop, no X close — the parent dialog owns close). In create
          mode it renders via MobileModal (UI-MOBILEMODAL-ADOPTION wave 2,
          2026-07-05) — z-[60] preserved so it still sits above the
          hand-rolled ItemDialog parent overlay. */}
      {showCategories && (findMode ? (
        <div className="">
          <div className="bg-gray-900 rounded-lg w-full max-h-[55vh] flex flex-col overflow-hidden border border-gray-700">
            {/* Header — find mode keeps only the optional back arrow + title;
                drops the X (parent dialog owns dismissal). */}
            <div className="p-3 bg-gray-800 border-b border-gray-700 flex justify-between items-center flex-shrink-0">
              {browserCategory && (
                <button
                  onClick={() => setBrowserCategory(null)}
                  className="text-gray-400 hover:text-white flex items-center gap-2 text-sm"
                >
                  ← Back
                </button>
              )}
              <h3 className="font-bold text-sm">
                {browserCategory ? (
                  browserCategory === 'packs' ? 'Starter Packs' : formatCategoryName(browserCategory)
                ) : 'Categories'}
              </h3>
              {!browserCategory && <span className="w-12" /> /* spacer for centering */}
            </div>

            {/* Content: Categories or Items */}
            <div className="p-4 space-y-2 overflow-y-auto flex-1">
              {/* STAGE 1: Show categories */}
              {!browserCategory && (
                <>
                  {Object.keys(BASE_ITEMS).map(cat => (
                    <button
                      key={cat}
                      onClick={() => setBrowserCategory(cat)}
                      className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{getCategoryIcon(cat)}</span>
                        <span>{formatCategoryName(cat)}</span>
                      </div>
                      <span className="text-gray-400 text-sm">
                        {allItems.filter(i => i.category === cat && i.type === 'item').length} items →
                      </span>
                    </button>
                  ))}
                  {showPacks && (
                    <button
                      onClick={() => setBrowserCategory('packs')}
                      className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🎁</span>
                        <span>Starter Packs</span>
                      </div>
                      <span className="text-gray-400 text-sm">
                        {Object.keys(STARTER_PACKS).length} packs →
                      </span>
                    </button>
                  )}
                </>
              )}

              {/* STAGE 2: Show items in selected category */}
              {browserCategory && browserCategory !== 'packs' && (
                <>
                  {categoryItems.length === 0 && (
                    <div className="text-center text-gray-400 py-8">
                      No items in this category
                    </div>
                  )}
                  {categoryItems.map(([itemKey, item]) => {
                    const isSelected = findMode && browserCategory === selectedCategory && itemKey === selectedItem;
                    return (
                    <button
                      key={itemKey}
                      onClick={() => {
                        onCategoryChange(browserCategory);
                        onItemChange(itemKey);
                        addToRecent(browserCategory, itemKey);
                        // [2026-05-25]: In findMode, KEEP the inline browser
                        // open after pick — the user may want to browse more
                        // before clicking Add Item. Currently Selected
                        // (in the parent dialog) reflects the pick.
                        if (!findMode) {
                          setBrowserCategory(null);
                          setShowCategories(false);
                        }
                      }}
                      className={`w-full p-3 ${isSelected ? 'bg-gray-700 border border-amber-500' : 'bg-gray-800 hover:bg-gray-700 border border-transparent'} rounded text-left transition-colors`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <span className="text-lg flex-shrink-0">{item.icon || '📄'}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{item.name || EquipmentResolver.formatDisplayName(itemKey) || itemKey.replace(/_/g, ' ')}</div>
                            {item.description && (
                              <div className="text-xs text-gray-400 mt-0.5">{item.description}</div>
                            )}
                            {/* [2026-05-25]: In findMode, render the full
                                property line (damage / AC / rarity / magic
                                bonus / attunement / charges / properties /
                                weight / value) so the user can compare
                                without opening each item. */}
                            {findMode && renderItemPropertyBadges(item, formatCurrency)}
                          </div>
                        </div>
                        {!findMode && (
                          <div className="text-xs text-gray-400 text-right flex-shrink-0">
                            <div>{item.size?.[0]}x{item.size?.[1]}</div>
                            <div>{formatCurrency(item.baseValue || 0)}</div>
                          </div>
                        )}
                      </div>
                    </button>
                    );
                  })}
                </>
              )}

              {/* STAGE 2: Show starter packs */}
              {browserCategory === 'packs' && (
                <>
                  {packItems.map(([packKey, pack]) => (
                    <button
                      key={packKey}
                      onClick={() => {
                        onCategoryChange('packs');
                        onItemChange(packKey);
                        addToRecent('packs', packKey);
                        setBrowserCategory(null);
                        setShowCategories(false);
                      }}
                      className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">🎁 {pack.name}</div>
                        <div className="text-sm text-gray-400">{formatCurrency(pack.cost)}</div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {pack.items?.length || 0} items
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <MobileModal
          isOpen={showCategories}
          onClose={closeCategoryBrowser}
          title={browserCategory ? (
            browserCategory === 'packs' ? 'Starter Packs' : formatCategoryName(browserCategory)
          ) : 'Categories'}
          showSaveButton={false}
          overlayClassName="z-[60]"
          maxWidthClass="max-w-md"
        >
          {/* [UI-MOBILEMODAL-ADOPTION wave 2 — 2026-07-05]: stage-2-only back
              arrow, relocated from the old hand-rolled header row. Title stays
              a plain string (category label / 'Categories'). */}
          {browserCategory && (
            <div className="mb-2">
              <button
                onClick={() => setBrowserCategory(null)}
                className="text-gray-400 hover:text-white flex items-center gap-2 text-sm"
              >
                ← Back
              </button>
            </div>
          )}

          <div className="space-y-2">
            {/* STAGE 1: Show categories */}
            {!browserCategory && (
              <>
                {Object.keys(BASE_ITEMS).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setBrowserCategory(cat)}
                    className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getCategoryIcon(cat)}</span>
                      <span>{formatCategoryName(cat)}</span>
                    </div>
                    <span className="text-gray-400 text-sm">
                      {allItems.filter(i => i.category === cat && i.type === 'item').length} items →
                    </span>
                  </button>
                ))}
                {showPacks && (
                  <button
                    onClick={() => setBrowserCategory('packs')}
                    className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">🎁</span>
                      <span>Starter Packs</span>
                    </div>
                    <span className="text-gray-400 text-sm">
                      {Object.keys(STARTER_PACKS).length} packs →
                    </span>
                  </button>
                )}
              </>
            )}

            {/* STAGE 2: Show items in selected category */}
            {browserCategory && browserCategory !== 'packs' && (
              <>
                {categoryItems.length === 0 && (
                  <div className="text-center text-gray-400 py-8">
                    No items in this category
                  </div>
                )}
                {categoryItems.map(([itemKey, item]) => (
                  <button
                    key={itemKey}
                    onClick={() => {
                      onCategoryChange(browserCategory);
                      onItemChange(itemKey);
                      addToRecent(browserCategory, itemKey);
                      setBrowserCategory(null);
                      setShowCategories(false);
                    }}
                    className="w-full p-3 bg-gray-800 hover:bg-gray-700 border border-transparent rounded text-left transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <span className="text-lg flex-shrink-0">{item.icon || '📄'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{item.name || EquipmentResolver.formatDisplayName(itemKey) || itemKey.replace(/_/g, ' ')}</div>
                          {item.description && (
                            <div className="text-xs text-gray-400 mt-0.5">{item.description}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 text-right flex-shrink-0">
                        <div>{item.size?.[0]}x{item.size?.[1]}</div>
                        <div>{formatCurrency(item.baseValue || 0)}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* STAGE 2: Show starter packs */}
            {browserCategory === 'packs' && (
              <>
                {packItems.map(([packKey, pack]) => (
                  <button
                    key={packKey}
                    onClick={() => {
                      onCategoryChange('packs');
                      onItemChange(packKey);
                      addToRecent('packs', packKey);
                      setBrowserCategory(null);
                      setShowCategories(false);
                    }}
                    className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">🎁 {pack.name}</div>
                      <div className="text-sm text-gray-400">{formatCurrency(pack.cost)}</div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {pack.items?.length || 0} items
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </MobileModal>
      ))}
    </div>
  );
};

export default SearchableItemSelector;